<template>
  <header class="header" :class="{ 'header--scrolled': isScrolled || !isHome, 'header--open': isMobileMenuOpen }">
    <div class="header__container container">
      <!-- Logo -->
      <router-link to="/" class="header__logo" @click="closeMobileMenu">
        <div class="header__logo-icon">
          <img src="/images/general/omega_logo.png" alt="Omega Metal Industries Logo" />
        </div>
        <div class="header__logo-text">
          <span class="header__logo-name">Omega Metal Industries</span>
          <span class="header__logo-tagline">Serving Since 1996</span>
        </div>
      </router-link>

      <!-- Desktop Navigation -->
      <nav class="header__nav">
        <ul class="header__nav-list">
          <li
            v-for="item in navItems"
            :key="item.path"
            class="header__nav-item"
          >
            <router-link
              :to="item.path"
              class="header__nav-link"
              :class="{ 'header__nav-link--active': isActive(item.path) }"
            >
              {{ item.label }}
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- Right side controls -->
      <div class="header__controls">
        <!-- Dark mode toggle -->
        <button
          class="header__theme-toggle"
          @click="toggleTheme"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          <svg v-if="isDark" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/>
            <line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/>
            <line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        </button>

        <!-- CTA Button -->
        <router-link to="/contact" class="header__cta btn btn--primary">
          Contact Us
        </router-link>
      </div>

      <!-- Mobile Menu Button -->
      <button
        class="header__mobile-toggle"
        @click="toggleMobileMenu"
        :aria-expanded="isMobileMenuOpen"
        aria-label="Toggle navigation menu"
      >
        <span class="header__mobile-toggle-line"></span>
        <span class="header__mobile-toggle-line"></span>
        <span class="header__mobile-toggle-line"></span>
      </button>
    </div>

    <!-- Mobile Menu -->
    <div class="header__mobile-menu" v-show="isMobileMenuOpen">
      <nav class="header__mobile-nav">
        <ul class="header__mobile-nav-list">
          <li v-for="item in navItems" :key="item.path">
            <router-link
              :to="item.path"
              class="header__mobile-nav-link"
              @click="closeMobileMenu"
            >
              {{ item.label }}
            </router-link>
          </li>
        </ul>

        <!-- Mobile theme toggle -->
        <div class="header__mobile-theme">
          <button class="header__mobile-theme-btn" @click="toggleTheme">
            <template v-if="isDark">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
              Light Mode
            </template>
            <template v-else>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
              Dark Mode
            </template>
          </button>
        </div>

        <router-link
          to="/contact"
          class="header__mobile-cta btn btn--primary btn--block"
          @click="closeMobileMenu"
        >
          Contact Us
        </router-link>
      </nav>
    </div>
  </header>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from '@/composables/useTheme.js'

export default {
  name: 'AppHeader',
  setup() {
    const route = useRoute()
    const isScrolled = ref(false)
    const isMobileMenuOpen = ref(false)

    const { isDark, toggleTheme, initTheme } = useTheme()

    const navItems = [
      { path: '/about', label: 'About Us' },
      { path: '/precision-machining', label: 'Precision Machining' },
      { path: '/fabrication', label: 'Fabrication' },
      { path: '/special-services', label: 'Special Services' },
      { path: '/qhse', label: 'QHSE Compliance' }
    ]

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 50
    }

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value
      document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
    }

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false
      document.body.style.overflow = ''
    }

    const isHome = computed(() => route.path === '/')

    const isActive = (path) => {
      if (path === '/') {
        return route.path === '/'
      }
      return route.path.startsWith(path)
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
      handleScroll()
      initTheme()
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      navItems,
      isHome,
      isScrolled,
      isMobileMenuOpen,
      toggleMobileMenu,
      closeMobileMenu,
      isActive,
      isDark,
      toggleTheme
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables' as *;
@use '@/assets/scss/mixins' as *;

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: $z-sticky;
  background-color: transparent;
  transition: all $transition-base;

  &--scrolled {
    background-color: var(--bg-header);
    box-shadow: $shadow-md;

    .header__logo-name {
      color: var(--text-primary);
    }

    .header__logo-tagline {
      color: var(--text-muted);
    }

    .header__nav-link {
      color: var(--text-secondary);

      &:hover {
        color: var(--text-primary);
        background-color: var(--bg-surface);
      }

      &--active {
        color: $accent-500;
      }
    }

    .header__mobile-toggle-line {
      background-color: var(--text-primary);
    }

    .header__theme-toggle {
      background: var(--bg-surface);
      color: var(--text-primary);

      &:hover {
        background: var(--border-color);
      }
    }
  }

  &--open {
    background-color: var(--bg-header);

    .header__logo-name {
      color: var(--text-primary);
    }

    .header__logo-tagline {
      color: var(--text-muted);
    }

    .header__mobile-toggle-line {
      background-color: var(--text-primary);
    }
  }

  &__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 80px;

    @include lg {
      height: 90px;
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    gap: $spacing-3;
    text-decoration: none;
  }

  &__logo-icon {
    height: 48px;
    width: auto;
    flex-shrink: 0;

    img {
      height: 100%;
      width: auto;
      object-fit: contain;
    }
  }

  &__logo-text {
    display: none;

    @include sm {
      display: flex;
      flex-direction: column;
    }
  }

  &__logo-name {
    font-size: $font-size-lg;
    font-weight: $font-weight-bold;
    color: $white;
    line-height: 1.2;
    transition: color $transition-base;
  }

  &__logo-tagline {
    font-size: $font-size-xs;
    color: rgba($white, 0.8);
    transition: color $transition-base;
  }

  &__nav {
    display: none;

    @include lg {
      display: block;
    }
  }

  &__nav-list {
    display: flex;
    align-items: center;
    gap: $spacing-1;
  }

  &__nav-item {
    position: relative;

    &--has-dropdown {
      // extra specificity handled via hover states
    }
  }

  &__nav-link {
    display: flex;
    align-items: center;
    gap: $spacing-1;
    padding: $spacing-2 $spacing-3;
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    color: rgba($white, 0.9);
    text-decoration: none;
    border-radius: $radius-md;
    transition: all $transition-fast;
    white-space: nowrap;

    &:hover {
      color: $white;
      background-color: rgba($white, 0.1);
    }

    &--active {
      color: $accent-500;
    }
  }

  // Controls group (theme toggle + WhatsApp)
  &__controls {
    display: none;
    align-items: center;
    gap: $spacing-3;

    @include lg {
      display: flex;
    }
  }

  &__cta {
    white-space: nowrap;
  }

  // Theme toggle button
  &__theme-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: $radius-full;
    border: none;
    background: rgba($white, 0.15);
    color: $white;
    cursor: pointer;
    transition: background-color $transition-fast, color $transition-fast;
    flex-shrink: 0;

    &:hover {
      background: rgba($white, 0.28);
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }

  // Mobile toggle button
  &__mobile-toggle {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 40px;
    height: 40px;
    padding: $spacing-2;
    background: none;
    border: none;
    cursor: pointer;

    @include lg {
      display: none;
    }
  }

  &__mobile-toggle-line {
    width: 24px;
    height: 2px;
    background-color: $white;
    border-radius: 2px;
    transition: all $transition-fast;
  }

  &--open &__mobile-toggle-line {
    &:nth-child(1) {
      transform: translateY(7px) rotate(45deg);
    }

    &:nth-child(2) {
      opacity: 0;
    }

    &:nth-child(3) {
      transform: translateY(-7px) rotate(-45deg);
    }
  }

  &__mobile-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--bg-mobile-menu);
    border-top: 1px solid var(--border-color);
    box-shadow: $shadow-lg;
    max-height: calc(100vh - 80px);
    overflow-y: auto;

    @include lg {
      display: none !important;
    }
  }

  &__mobile-nav {
    padding: $spacing-6;
  }

  &__mobile-nav-list {
    margin-bottom: $spacing-4;
  }

  &__mobile-nav-link {
    display: block;
    padding: $spacing-3 0;
    font-size: $font-size-lg;
    font-weight: $font-weight-medium;
    color: var(--text-primary);
    text-decoration: none;
    border-bottom: 1px solid var(--border-light);
    width: 100%;
    text-align: left;
    background: none;
    border-left: none;
    border-right: none;
    border-top: none;
    cursor: pointer;
    font-family: inherit;

    &:hover {
      color: $primary-700;
    }
  }

  // Mobile theme toggle row
  &__mobile-theme {
    margin-bottom: $spacing-4;
    padding-bottom: $spacing-4;
    border-bottom: 1px solid var(--border-light);
  }

  &__mobile-theme-btn {
    display: flex;
    align-items: center;
    gap: $spacing-3;
    padding: $spacing-2 $spacing-3;
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: $radius-lg;
    color: var(--text-primary);
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    cursor: pointer;
    font-family: inherit;
    transition: background-color $transition-fast;

    &:hover {
      background: var(--border-color);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
